// Minimal BibTeX display parser for the Writer bibliography panel.
//
// The Writer stores canonical BibTeX in `writer_bib_entries.bibtex` (one row
// per project + citekey). For the panel we only need pretty display fields
// (title / authors / year / venue / doi / url) — we do NOT try to be a
// full BibTeX parser. Anything we can't parse falls back to the raw source.

export interface BibDisplay {
  citekey: string;
  title: string;
  authors: string[];
  year: number | null;
  venue: string | null;
  doi: string | null;
  url: string | null;
  raw: string;
}

/** Strip {} groups and trim, keeping the inner text. */
function unbrace(v: string): string {
  return v.replace(/[{}]/g, '').trim();
}

/**
 * Extract a top-level `field = value` pair from a BibTeX entry body. Values
 * may be brace-wrapped, quote-wrapped, or a bare number.
 */
function extractField(body: string, name: string): string | null {
  const re = new RegExp(`\\b${name}\\s*=\\s*(\\{([^{}]*(?:\\{[^{}]*\\}[^{}]*)*)\\}|"([^"]*)"|(\\d+))`, 'i');
  const m = body.match(re);
  if (!m) return null;
  const raw = m[2] ?? m[3] ?? m[4] ?? '';
  return unbrace(raw);
}

/** Split an `author` field on `and` respecting the standard BibTeX convention. */
function splitAuthors(field: string): string[] {
  return field
    .split(/\s+and\s+/i)
    .map((a) => unbrace(a).trim())
    .filter((a) => a.length > 0)
    .slice(0, 32);
}

/**
 * Parse a single BibTeX entry into display fields. If the source can't be
 * matched we still return a usable object with the citekey and raw source so
 * the panel never renders an empty row.
 */
export function parseBibtexForDisplay(citekey: string, bibtex: string): BibDisplay {
  const empty: BibDisplay = {
    citekey,
    title: '',
    authors: [],
    year: null,
    venue: null,
    doi: null,
    url: null,
    raw: bibtex,
  };
  if (!bibtex) return empty;
  // Strip the header `@type{key,` and trailing `}` for field extraction.
  const bodyMatch = bibtex.match(/@\w+\s*\{\s*[^,]+,([\s\S]*)\}\s*$/);
  const body = bodyMatch?.[1] ?? bibtex;
  const title = extractField(body, 'title') ?? '';
  const authors = splitAuthors(extractField(body, 'author') ?? '');
  const yearStr = extractField(body, 'year');
  const year = yearStr && /^\d{3,4}$/.test(yearStr) ? Number(yearStr) : null;
  const venue =
    extractField(body, 'journal') ??
    extractField(body, 'booktitle') ??
    extractField(body, 'publisher') ??
    null;
  const doi = extractField(body, 'doi');
  const url = extractField(body, 'url');
  return { citekey, title, authors, year, venue, doi, url, raw: bibtex };
}
